import { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import {
  Checkbox,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tooltip,
} from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'
import ClearIcon from '@mui/icons-material/Clear'

import Dialog from './Dialog'
import GridHeader from './GridHeader'
import InputField from './InputField'
import TableCellAddress from './table/TableCellAddress'
import SortableTableHead from './SortableTableHead'
import GridCircularProgress from './GridCircularProgress'
import { colours, titlesRimborso } from '../settings/settings'
import { useGetDesignazioniByArbitroRimborsoQuery } from '../hooks/api-slice'
import { sumFun, formatEuro } from '../hooks/utils-functions'

const DialogRimborso = ({
  open,
  handleClose,
  triggerFunction,
  rimborsoInit,
  mode,
}) => {
  const { admin, arbitro, idArbitro } = useSelector(
    state => state.home.infoUtente,
  )

  const [selectedDesignazioni, setSelectedDesignazioni] = useState()
  const [rimborso, setRimborso] = useState()

  const isAdd = mode === 'add'
  const isUpdate = mode === 'update'
  const isView = mode === 'view'

  const { data: availableDesignazioni, isLoading } =
    useGetDesignazioniByArbitroRimborsoQuery(
      {
        idArbitro: arbitro ? idArbitro : rimborso?.idArbitro,
        urlSuffix: !isAdd ? '/all' : '',
      },
      { skip: !(open && ((arbitro && idArbitro) || rimborso?.idArbitro)) },
    )

  let columnsRimborsoDesignazioni = []
  if (isAdd)
    columnsRimborsoDesignazioni = [
      ...columnsRimborsoDesignazioni,
      { label: 'Selezione', sortable: false },
    ]
  columnsRimborsoDesignazioni = [
    ...columnsRimborsoDesignazioni,
    { label: 'Data e Ora', sortable: false },
    { label: 'Categoria', sortable: false },
    { label: 'Gara', sortable: false },
    { label: 'Indirizzo', sortable: false },
  ]
  if (!isAdd)
    columnsRimborsoDesignazioni = [
      ...columnsRimborsoDesignazioni,
      { label: 'Diaria', sortable: false },
      { label: 'km percorsi', sortable: false },
      { label: 'Spese', sortable: false },
      { label: 'Totale', sortable: false },
    ]

  const MAX_GARE = 12

  // initialize rimborso
  useEffect(() => {
    if (open) setRimborso(rimborsoInit)
  }, [open, rimborsoInit])

  // initialize selectedDesignazioni
  useEffect(() => {
    setSelectedDesignazioni(rimborso?.designazioni?.map(d => d.idDesignazione))
  }, [rimborso])

  const dFind = id => rimborso?.designazioni?.find(i => i.idDesignazione === id)

  const aField = (id, field, strict) => {
    if (
      !strict &&
      field === 'kmPercorsi' &&
      typeof aField(id, 'kmPercorsiToValidate') === 'number'
    )
      field = 'kmPercorsiToValidate'

    return dFind(id)?.[field]
  }

  const checkKm = id => {
    const aKm1 = aField(id, 'kmPercorsi', true)
    const aKm2 = aField(id, 'kmPercorsiToValidate', true)
    const aKm3 = aField(id, 'kmGoogleMaps', true)

    return (
      typeof aKm2 === 'number' &&
      aKm2 !== aKm1 &&
      (typeof aKm3 !== 'number' || aKm3 !== aKm2)
    )
  }

  const dUpdateField = (id, field, value) => {
    if (
      !admin &&
      field === 'kmPercorsi' &&
      typeof aField(id, 'kmGoogleMaps') === 'number'
    )
      field = 'kmPercorsiToValidate'

    return rimborso?.designazioni?.map(i =>
      i.idDesignazione === id ? { ...i, [field]: value } : i,
    )
  }

  const acceptKm = id => {
    const kmToValidate = aField(id, 'kmPercorsiToValidate', true)
    setRimborsoField(
      'designazioni',
      rimborso?.designazioni?.map(i =>
        i.idDesignazione === id
          ? { ...i, kmPercorsi: kmToValidate, kmPercorsiToValidate: undefined }
          : i,
      ),
    )
  }

  const rejectKm = id =>
    setRimborsoField(
      'designazioni',
      rimborso?.designazioni?.map(i =>
        i.idDesignazione === id ? { ...i, kmPercorsiToValidate: undefined } : i,
      ),
    )

  const totGara = id =>
    (aField(id, 'diaria') || 0) +
    (aField(id, 'kmPercorsi') || 0) * rimborso?.costoKm +
    (aField(id, 'spese') || 0)

  const rimbDesiMapField = field =>
    rimborso?.designazioni?.map(i => aField(i.idDesignazione, field))

  const totDiaria = () => sumFun(rimbDesiMapField('diaria')) || 0
  const totKmPercorsi = () => sumFun(rimbDesiMapField('kmPercorsi')) || 0
  const totSpese = () => sumFun(rimbDesiMapField('spese')) || 0

  const totCompenso = () => totDiaria()
  const totRimborsoSpese = () =>
    totKmPercorsi() * rimborso?.costoKm + totSpese()
  const totTotale = () => totCompenso() + totRimborsoSpese()

  const setRimborsoField = (field, value) =>
    setRimborso(prev => ({ ...prev, [field]: value }))

  useEffect(() => {
    if (!open) {
      setRimborso()
      setSelectedDesignazioni()
    }
  }, [open])

  return (
    <Dialog
      open={open}
      handleClose={handleClose}
      triggerFunction={() =>
        triggerFunction(
          isAdd ? selectedDesignazioni : isUpdate ? rimborso : undefined,
        )
      }
      title={titlesRimborso.titleRimborso}
      textUndo={(isView && 'Chiudi') || 'Annulla'}
      textConfirm={!isView && 'Salva'}
      stretchWidth
      stretchHeight
    >
      <Grid container spacing={2} sx={{ width: '100%' }}>
        <Grid size={12}>
          {isAdd && `Selezionare fino a ${MAX_GARE} gare.`}
          {isUpdate &&
            'I dati inseriti potranno essere modificati finché non verrà effettuato il pagamento.'}
          {isUpdate && !admin && (
            <span>
              <br />I km evidenziati in giallo devono essere validati dalla
              segreteria.
            </span>
          )}
          {isView &&
            'I dati inseriti non possono più essere modificati perché è già stato effettuato il pagamento.'}
        </Grid>
        {(isLoading && <GridCircularProgress />) ||
          (!availableDesignazioni && (
            <GridHeader text='Elenco delle gare non trovato.' />
          )) ||
          (availableDesignazioni.length === 0 && (
            <h4>Nessuna gara {isAdd ? 'da rimborsare' : 'trovata'}.</h4>
          )) || (
            <Grid container alignItems='center' sx={{ width: '100%' }}>
              {isAdd && (
                <Grid size={12}>
                  <center>
                    <b>
                      {selectedDesignazioni?.length === 1
                        ? '1 gara selezionata'
                        : `${selectedDesignazioni?.length || 0} gare selezionate`}
                    </b>
                  </center>
                </Grid>
              )}
              {!isAdd &&
                (typeof rimborso?.importoLordoAnnuoPGS === 'number' ||
                  typeof rimborso?.importoLordoAnnuoAltri === 'number') && (
                  <>
                    <Grid
                      container
                      spacing={2}
                      alignItems='center'
                      sx={{ width: '100%' }}
                    >
                      <Grid>
                        Importo lordo già ricevuto da PGS dal 1° Gennaio ai
                        sensi dell'Art. 25 e ss. (D.Lgs 36/2021):
                      </Grid>
                      <Grid size={{ xs: 8, sm: 4, md: 3, lg: 2 }}>
                        {isView ? (
                          formatEuro(rimborso?.importoLordoAnnuoPGS)
                        ) : (
                          <InputField
                            id='importoLordoAnnuoPGS'
                            align='center'
                            variant='standard'
                            placeholder='0.00'
                            value={rimborso?.importoLordoAnnuoPGS}
                            onChange={x =>
                              setRimborsoField(
                                'importoLordoAnnuoPGS',
                                +x?.toFixed(2),
                              )
                            }
                            type='number'
                          />
                        )}
                      </Grid>
                    </Grid>
                    <Grid
                      container
                      spacing={2}
                      alignItems='center'
                      sx={{ width: '100%' }}
                    >
                      <Grid>
                        Importo lordo già ricevuto da altri dal 1° Gennaio ai
                        sensi dell'Art. 25 e ss. (D.Lgs 36/2021):
                      </Grid>
                      <Grid size={{ xs: 8, sm: 6, md: 4, lg: 2 }}>
                        {isView ? (
                          formatEuro(rimborso?.importoLordoAnnuoAltri)
                        ) : (
                          <InputField
                            id='importoLordoAnnuoAltri'
                            align='center'
                            variant='standard'
                            placeholder='0.00'
                            value={rimborso?.importoLordoAnnuoAltri}
                            onChange={x =>
                              setRimborsoField(
                                'importoLordoAnnuoAltri',
                                +x?.toFixed(2),
                              )
                            }
                            type='number'
                          />
                        )}
                      </Grid>
                    </Grid>
                  </>
                )}
              {!isAdd &&
                [
                  `Totale compenso: ${formatEuro(totCompenso() || 0)}`,
                  `Totale spese: ${formatEuro(totRimborsoSpese() || 0)}`,
                  `Totale: ${formatEuro(totTotale() || 0)}`,
                ].map((t, index) => (
                  <Grid key={index} size={{ xs: 12, md: 4 }}>
                    <center>
                      <b>{t}</b>
                    </center>
                  </Grid>
                ))}
              <TableContainer component={Paper}>
                <Table size='small'>
                  <SortableTableHead
                    table={availableDesignazioni}
                    columns={columnsRimborsoDesignazioni}
                  />
                  <TableBody>
                    {availableDesignazioni
                      ?.filter(
                        a => isAdd || selectedDesignazioni?.includes(a._id),
                      )
                      ?.map((a, index) => {
                        const isSelected = selectedDesignazioni?.includes(a._id)

                        const aDiaria = aField(a._id, 'diaria')
                        const aKm = aField(a._id, 'kmPercorsi')
                        const aKm1 = aField(a._id, 'kmPercorsi', true)
                        const aKm2 = aField(a._id, 'kmPercorsiToValidate', true)
                        const aSpese = aField(a._id, 'spese')
                        const aCheckKm = checkKm(a._id)

                        const cellProps = {
                          sx: {
                            borderBottomColor:
                              isAdd &&
                              index < availableDesignazioni.length - 1 &&
                              availableDesignazioni[index]?.data?.split(
                                '/',
                              )?.[1] !==
                                availableDesignazioni[index + 1]?.data?.split(
                                  '/',
                                )?.[1] &&
                              colours.greyDark,
                          },
                        }

                        const cellPropsFixedSx = { width: '10vw' }

                        return (
                          <TableRow
                            key={index}
                            sx={{
                              backgroundColor:
                                isAdd && isSelected
                                  ? colours.highlightGreen
                                  : colours.white,
                            }}
                          >
                            {isAdd && (
                              <TableCell {...cellProps}>
                                <Checkbox
                                  id={`${a._id}-checkbox`}
                                  color='success'
                                  checked={!!isSelected}
                                  onChange={() =>
                                    setSelectedDesignazioni(
                                      isSelected
                                        ? selectedDesignazioni?.filter(
                                            g => g !== a._id,
                                          )
                                        : !selectedDesignazioni
                                          ? [a._id]
                                          : selectedDesignazioni?.length <
                                              MAX_GARE
                                            ? [...selectedDesignazioni, a._id]
                                            : selectedDesignazioni,
                                    )
                                  }
                                />
                              </TableCell>
                            )}
                            <TableCell {...cellProps}>
                              {a?.data}&nbsp;{a?.ora}
                            </TableCell>
                            <TableCell {...cellProps}>{a?.categoria}</TableCell>
                            <TableCell {...cellProps}>
                              {a?.numeroGara}
                            </TableCell>
                            <TableCellAddress {...cellProps} gara={a} />
                            {!isAdd && (
                              <TableCell
                                {...cellProps}
                                sx={{
                                  ...cellProps.sx,
                                  ...cellPropsFixedSx,
                                  borderLeftWidth: 1,
                                  borderLeftStyle: 'solid',
                                  borderLeftColor: colours.greyDark,
                                }}
                              >
                                {formatEuro(aDiaria)}
                              </TableCell>
                            )}
                            {!isAdd && (
                              <TableCell
                                {...cellProps}
                                sx={{
                                  ...cellProps.sx,
                                  ...cellPropsFixedSx,
                                  backgroundColor:
                                    aCheckKm && colours.highlight,
                                }}
                              >
                                {isView ? (
                                  aKm
                                ) : admin && aCheckKm ? (
                                  <center>
                                    <IconButton onClick={() => acceptKm(a._id)}>
                                      <Tooltip title={`Accetta ${aKm2}`}>
                                        <CheckIcon color='success' />
                                      </Tooltip>
                                    </IconButton>
                                    <b>{aKm2}</b>
                                    &nbsp;
                                    <span
                                      style={{ textDecoration: 'line-through' }}
                                    >
                                      {aKm1}
                                    </span>
                                    <IconButton onClick={() => rejectKm(a._id)}>
                                      <Tooltip title={`Mantieni ${aKm1}`}>
                                        <ClearIcon color='error' />
                                      </Tooltip>
                                    </IconButton>
                                  </center>
                                ) : (
                                  <Grid size='grow'>
                                    <InputField
                                      id={`${a._id}-kmPercorsi`}
                                      align='center'
                                      variant='standard'
                                      placeholder='0'
                                      value={aKm || undefined}
                                      onChange={x =>
                                        (!x || x < 1000) &&
                                        setRimborsoField(
                                          'designazioni',
                                          dUpdateField(
                                            a._id,
                                            'kmPercorsi',
                                            +x?.toFixed(0),
                                          ),
                                        )
                                      }
                                      type='number'
                                    />
                                  </Grid>
                                )}
                              </TableCell>
                            )}
                            {!isAdd && (
                              <TableCell
                                {...cellProps}
                                sx={{ ...cellProps.sx, ...cellPropsFixedSx }}
                              >
                                {isView ? (
                                  formatEuro(aSpese)
                                ) : (
                                  <InputField
                                    id={`${a._id}-spese`}
                                    align='center'
                                    variant='standard'
                                    placeholder='0.00'
                                    value={aSpese || undefined}
                                    onChange={x =>
                                      (!x || x < 1000) &&
                                      setRimborsoField(
                                        'designazioni',
                                        dUpdateField(
                                          a._id,
                                          'spese',
                                          +x?.toFixed(2),
                                        ),
                                      )
                                    }
                                    type='number'
                                  />
                                )}
                              </TableCell>
                            )}
                            {!isAdd && (
                              <TableCell
                                {...cellProps}
                                sx={{ ...cellProps.sx, ...cellPropsFixedSx }}
                              >
                                {formatEuro(totGara(a._id) || 0)}
                              </TableCell>
                            )}
                          </TableRow>
                        )
                      })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          )}
      </Grid>
    </Dialog>
  )
}

export default DialogRimborso
